import rawGnawballNames from "./gnawballNames.txt?raw";
import type { NameTriple } from "./NameGenerator.ts";
import rawOrcNames from "./names.txt?raw";
import type {
  FactionProfile,
  GearProfile,
  GitProfile,
  GitSpecialRule,
  ModProfile,
  NameGeneratorData,
  RepairLocationProfile,
  RulesetConstants,
  TreatmentLocationProfile,
  VehicleProfile,
  VehicleSpecialRule,
  WeaponProfile,
  WeaponSpecialRule,
} from "./Ruleset.ts";

export const constants: RulesetConstants = {
  defaultCarryCapacity: 2,
  defaultAttitude: 1,
  maxAttitude: 6,
  aLaCarteCapsBudget: 25,
  chefsRecMaxModelsCost: 20,
  chaosModeCostThreshold: 20,
  downtroddenItemCostThreshold: 1,
  campaignModeCostMultiplier: 2,
};

export const gits: GitProfile[] = [
  {
    id: "orc",
    name: "Orc",
    cost: 5,
    selectOn: [6, 7, 8],
    scoot: { type: "D6", count: 3 },
    headbutt: "D6",
    toughness: 3,
    canDrive: true,
  },
  {
    id: "bullorc",
    name: "Bullorc",
    cost: 7,
    selectOn: [9],
    scoot: { type: "D6", count: 3 },
    headbutt: "D8",
    toughness: 4,
    rules: [{ id: "strong" }],
    canDrive: true,
  },
  {
    id: "goblin",
    name: "Goblin",
    cost: 4,
    selectOn: [5],
    scoot: { type: "D6", count: 3 },
    headbutt: "D4",
    toughness: 3,
    rules: [{ id: "downtrodden" }, { id: "lightweight" }, { id: "numerous" }],
    canDrive: true,
  },
  {
    id: "gnawball",
    name: "Gnawball",
    cost: 4,
    selectOn: [3, 4],
    scoot: [
      { type: "D6", count: 1 },
      { type: "D8", count: 1 },
      { type: "D10", count: 1 },
    ],
    headbutt: "D10",
    toughness: 3,
    rules: [{ id: "animal" }, { id: "numerous" }, { id: "rigged" }],
    canDrive: false,
  },
  {
    id: "runt",
    name: "Runt",
    cost: 4,
    selectOn: [2],
    scoot: { type: "D6", count: 3 },
    headbutt: "D4",
    toughness: 2,
    rules: [
      { id: "animal" },
      { id: "eager_helper" },
      { id: "numerous" },
      { id: "puny" },
    ],
    canDrive: false,
  },
  {
    id: "klankbot",
    name: "Klankbot",
    cost: 10,
    selectOn: [10],
    scoot: { type: "D6", count: 2 },
    headbutt: "D12",
    toughness: 5,
    rules: [{ id: "strong" }],
    canDrive: false,
  },
];

export const gitSpecialRules: GitSpecialRule[] = [
  {
    id: "strong",
    name: "Strong",
    description: "Can hold 2-handed weapons in one hand.",
  },
  {
    id: "downtrodden",
    name: "Downtrodden",
    description:
      "You can’t buy this git any weapon or fancy gear that costs more than 1 cap.",
  },
  {
    id: "lightweight",
    name: "Lightweight",
    description: "When this git takes a swig, add an extra die.",
  },
  {
    id: "numerous",
    name: "Numerous",
    description:
      "The first git you deploy with this rule doesn’t count against your 5 maximum models.",
  },
  {
    id: "animal",
    name: "Animal",
    description: "You cannot give this git any weapons or fancy gear.",
  },
  {
    id: "eager_helper",
    name: "Eager Helper",
    description:
      "After a friendly git resolves a scoot die that lies within 6” of this git, this git may also scoot using that scoot die before it is discarded.",
  },
  {
    id: "rigged",
    name: "Rigged",
    description:
      "If this gnawball has explosives modelled onto them, they are rigged and if they fall over, they explode (7). Remove them from play afterwards. They count as fallen at the end of the game.",
  },
  {
    id: "puny",
    name: "Puny",
    description: "When you add a die to this git’s tower, add an extra die.",
  },
  {
    id: "bad_attitude",
    name: "Bad Attitude",
    description:
      "Once a git has an attitude of 6, they start acting like a right prick. Before each game, roll a number of D6 equal to their attitude. If they roll any ones, they can’t even be arsed to turn up to the game and skip it. You can still deploy up to five models, if you have others in your gang.",
    isCampaignRule: true,
  },
];

export const gear: GearProfile[] = [
  {
    id: "ammo_box",
    name: "Ammo Box",
    cost: 1,
    description:
      "If this git is modelled to be carrying some kind of crate or is bestrewn with ammunition belts, both this git and friendly models within 6” of this model may re-roll any of their bullet dice once per activation.",
    selectOn: [1, 2],
  },
  {
    id: "banner_ad",
    name: "Banner Ad",
    cost: 2,
    description:
      "If this git is modelled with a banner, and is upright at the end of the game, you gain D3-1 caps in advertising revenue.",
    selectOn: [3, 4],
  },
  {
    id: "bayonet",
    name: "Bayonet",
    cost: 1,
    description:
      "If this git’s gun has a nasty-looking blade attached, it counts as a Crude Hand Weapon as well.",
    selectOn: [5, 6],
  },
  {
    id: "beast_mount",
    name: "Beast Mount/Pogo Stick",
    cost: 2,
    description:
      "If this git is modelled to be riding some kind of wild animal, when they scoot, they roll D6, D8 & D10 as their scoot dice. However, after you roll your scoot dice, the player to your left also throws 2D6 into the play area. You use the three highest dice of the five as your scoot dice, and discard the other two. (If tied, you pick which dice to keep). This model can’t board vehicles.",
    selectOn: [7, 8],
  },
  {
    id: "big_pockets",
    name: "Big Pockets",
    cost: 1,
    description:
      "If this git is modelled with a big rucksack or overcoat they could stash a shotgun in, they may carry a third weapon (but still only wield what will fit in their hands).",
    selectOn: [9, 10],
    effects: [{ type: "INCREMENT_STAT", stat: "carryCapacity", value: 1 }],
  },
  {
    id: "big_hat",
    name: "Big Hat",
    cost: 2,
    description:
      "If this git is modelled with an item of impressive and commanding headgear, after they activate: a friendly git within 6” may take a swig to immediately activate. That second git can’t trigger a third model to activate.",
    selectOn: [11, 12],
  },
  {
    id: "bionic_arm",
    name: "Bionic Arm",
    cost: 2,
    description:
      "If this git is modelled with a mechanical arm of some sort, this git can count one of their 2-handed weapons as needing only 1 hand.",
    selectOn: [13, 14],
  },
  {
    id: "bionic_legs",
    name: "Bionic Leg(s)",
    cost: 1,
    description:
      "If this git is modelled with robotic leg(s) of some sort, when you scoot, you may treat any scoot dice as a 3.",
    selectOn: [15, 16],
  },
  {
    id: "bully_boots",
    name: "Bully Boots",
    cost: 3,
    description:
      "If this git is modelled with big boots, when this git puts the boot in, they nick D3 caps instead of 1.",
    selectOn: [17, 18],
  },
  {
    id: "cans",
    name: "Cans",
    cost: 1,
    description:
      "If this git is modelled to be wearing headphones or carrying a boombox, when they scoot, they add 1 to their scoot dice, as they strut to the beat.",
    selectOn: [19, 20],
  },
  {
    id: "chunky_armour",
    name: "Chunky Armour",
    cost: 3,
    description:
      "If this git is modelled with heavy-duty looking armour, they increase their toughness by +1.",
    selectOn: [21, 22],
    effects: [{ type: "INCREMENT_STAT", stat: "toughness", value: 1 }],
  },
  {
    id: "dapper_hairdo",
    name: "Dapper Hairdo",
    cost: 2,
    description:
      "If this git is assertively hirsute, they gain +1 attitude upon the acquisition of their fine ‘do. Such coiffeurary may be proudly displayed upon their head, lip, chin or ... no, just those places.",
    selectOn: [23, 24],
    effects: [{ type: "INCREMENT_STAT", stat: "attitude", value: 1 }],
  },
  {
    id: "fuel_can",
    name: "Fuel Can",
    cost: 1,
    description:
      "If this git is modelled to be carrying some kind of fuel can, bucket or bottle, while within 6” of this model, other friendly vehicles or Klankbots may re-roll any of their scoot dice once per activation. Once per game, you can permanently discard this upgrade to toss it onto a model within 3” that is on fire: that model immediately suffers 3D6 damage.",
    selectOn: [25, 26],
  },
  {
    id: "handcuffs",
    name: "Handcuffs",
    cost: 2,
    description:
      "If this git is modelled to be carrying rope or cuffs, enemy gits within 1” of this model cannot stand up.",
    selectOn: [27, 28],
  },
  {
    id: "hook_hand",
    name: "Hook Hand",
    cost: 0,
    description:
      "If this git is modelled with a hook for a hand, they have one fewer hands but when they shove, they can move themselves 3” in any direction (instead of shoving their opponent).",
    selectOn: [29, 30],
  },
  {
    id: "horns",
    name: "Horns",
    cost: 1,
    description:
      "If this git is modelled with horns, or horned headgear, it gets a free headbutt before the first throw in each fight.",
    selectOn: [31, 32],
  },
  {
    id: "improvised_shield",
    name: "Improvised Shield",
    cost: 1,
    description:
      "If this git has a scavenged shield, their boss can ask for a re-throw once per fight. The current throw is ignored, and you both throw again. The shield takes up one hand during fights.",
    selectOn: [33, 34],
  },
  {
    id: "jetpack",
    name: "Jetpack",
    cost: 3,
    description:
      "If this git is modelled with a jetpack, when it scoots, it can blast off instead of scooting normally. Roll a single D6 scoot die anywhere in play. Multiply the scoot die roll by 5”, pick the git up and place them that far away in the direction of that scoot dice, ignoring intervening models, dice and terrain. Push their tower back into contact with them (along whatever route is needed) without it falling. If their tower does falls while being moved, they explode (6) at their landing spot.",
    selectOn: [35, 36],
  },
  {
    id: "kitbashed_weapon",
    name: "Kitbashed Weapon",
    cost: 2,
    description:
      "If this git is modelled with some kind of kludged-together double-gun, you may say it is a “kitbashed weapon”. When this weapon rolls a dud, it doesn’t miss, instead that bullet gains the gout special rule.",
    selectOn: [37, 38],
  },
  {
    id: "massive_spanner",
    name: "Massive Spanner",
    cost: 1,
    description:
      "If this git is carrying some kind of hefty tool (fnar), this git can take an action to bonk another vehicle or klankbot within 1”: remove D6 dice from the bonked model’s tower.",
    selectOn: [39, 40],
  },
  {
    id: "medical_equipment",
    name: "Medical Equipment",
    cost: 1,
    description:
      "If this git is carrying some kind of doctor’s kit or big syringe, they may take an action to experiment on another git (friend or foe) within 1”. Roll on the Vending Machine table and apply the result to the target. If a 6 is rolled, the target suffers D6 damage instead of gaining caps.",
    selectOn: [41, 42],
  },
  {
    id: "megaphone",
    name: "Megaphone",
    cost: 2,
    description:
      "If this git is modelled with a megaphone or loudhailer, they can take an action to bark orders down it: select a friendly git within 3D6”, they immediately take one action.",
    selectOn: [43, 44],
  },
  {
    id: "mono_wheel",
    name: "Mono-Wheel",
    cost: 1,
    description:
      "If this git is modelled with a wheel instead of legs, when you scoot, you can take a swig to discard one of your scoot dice and push its value in any direction.",
    selectOn: [45, 46],
  },
  {
    id: "musical_instrument",
    name: "Musical Instrument",
    cost: 1,
    description:
      "If this git is modelled to be carrying some kind of musical instrument, this git may take an action to rock out. When your git rocks out, push every friendly git within D6” up to 3”. If a pushed git bumps into another git, that second git is pushed directly away 3”. (If a pushed git ends up stuck in contact with an enemy end the action and the activation and have a fight).",
    selectOn: [47, 48],
  },
  {
    id: "oversized_gun",
    name: "Oversized Gun",
    cost: 1,
    description:
      "If this git is modelled with some kind of ludicrously oversized shooter, that weapon counts whatever is hit as having -1 toughness.",
    selectOn: [49, 50],
  },
  {
    id: "skull_cap",
    name: "Skull Cap",
    cost: 1,
    description:
      "If this git is modelled with a metal head, improve its headbutt stat by one dice rank (e.g. D6 becomes D8) up to a maximum of D12.",
    selectOn: [51, 52],
    effects: [{ type: "INCREMENT_STAT", stat: "headbutt", value: 1 }],
  },
  {
    id: "spikey_bits",
    name: "Spikey Bits",
    cost: 1,
    description:
      "When fighting, if your thump is cancelled out by a shove, you still get to thump, but only using your headbutt thump dice.",
    selectOn: [53, 54],
  },
  {
    id: "steel_jaw",
    name: "Steel Jaw",
    cost: 1,
    description:
      "If this git is modelled with a metal jaw or face, they get +1 toughness when thumped in a fight.",
    selectOn: [55, 56],
  },
  {
    id: "strong_booze",
    name: "Strong Booze",
    cost: 1,
    description:
      "If this git is carrying a bottle of strong liquor, once per game, after activating, they may take a swig D6 times to immediately activate again.",
    selectOn: [57, 58],
  },
  {
    id: "tasty_snack",
    name: "Tasty Snack",
    cost: 1,
    description:
      "If this git is carrying some greasy foodstuff, once per game, if this git has fallen, they may be selected to activate, stand up, and then gain D8 damage.",
    selectOn: [59, 60],
  },
  {
    id: "tracks",
    name: "Tracks",
    cost: 1,
    description:
      "If this git is modelled with tracks instead of legs, when resolving a scoot die, you may discard another of your scoot die to add its value to this one.",
    selectOn: [61, 62],
  },
];

export const vehicles: VehicleProfile[] = [
  {
    id: "forklift",
    name: "Forklift",
    cost: 3,
    selectOn: [2],
    engine: "D6",
    toughness: 4,
    seats: 1,
    mods: [],
    rules: [],
  },
  {
    id: "truck",
    name: "Truck",
    cost: 8,
    selectOn: [3],
    engine: "D8",
    toughness: 5,
    seats: 6,
    mods: [],
    rules: [],
  },
  {
    id: "bike_with_sidecar",
    name: "Bike with Sidecar",
    cost: 5,
    selectOn: [5],
    engine: "D12",
    toughness: 3,
    seats: 2,
    mods: [],
    rules: [],
  },
  {
    id: "bike",
    name: "Bike",
    cost: 4,
    selectOn: [5, 6],
    engine: "D12",
    toughness: 3,
    seats: 1,
    mods: [],
    rules: [],
  },
  {
    id: "buggy",
    name: "Buggy",
    cost: 6,
    selectOn: [7, 8],
    engine: "D12",
    toughness: 3,
    seats: 2,
    mods: [],
    rules: [],
  },
  {
    id: "half_track",
    name: "Half-Track",
    cost: 6,
    selectOn: [9],
    engine: "D8",
    toughness: 4,
    seats: 3,
    mods: [{ id: "tank_tracks" }],
    rules: [],
  },
  {
    id: "car",
    name: "Car",
    cost: 8,
    selectOn: [10],
    engine: "D10",
    toughness: 4,
    seats: 4,
    mods: [],
    rules: [],
  },
  {
    id: "buzzcopter",
    name: "Buzzcopter",
    cost: 12,
    selectOn: [11],
    engine: "D12",
    toughness: 2,
    seats: 1,
    mods: [],
    rules: [{ id: "buzzcopter" }],
  },
  {
    id: "scrap_tank",
    name: "Scrap Tank",
    cost: 12,
    selectOn: [12],
    engine: "D8",
    toughness: 6,
    seats: 4,
    mods: [{ id: "tank_tracks" }],
    rules: [],
  },
];

export const weapons: WeaponProfile[] = [
  // Ranged

  {
    id: "pistol",
    name: "Pistol",
    cost: 0,
    hands: 1,
    range: 9,
    dice: { type: "D6", count: 2 },
    damage: { weak: 1, big: 2 },
    rules: [],
    selectOnGit: [],
    selectOnVehicle: [],
  },
  {
    id: "blaster",
    name: "Blaster",
    cost: 0,
    hands: 2,
    range: 18,
    dice: { type: "D8", count: 1 },
    damage: { weak: 2, big: 3 },
    rules: [],
    selectOnGit: [],
    selectOnVehicle: [],
  },
  {
    id: "smg",
    name: "SMG",
    cost: 1,
    hands: 1,
    range: 9,
    dice: { type: "D6", count: 4 },
    damage: { weak: 1, big: 2 },
    rules: [],
    selectOnGit: [63, 64],
    selectOnVehicle: [],
  },
  {
    id: "shotgun",
    name: "Shotgun",
    cost: 1,
    hands: 2,
    range: 6,
    dice: { type: "D8", count: 3 },
    damage: { weak: 2, big: 3 },
    rules: [],
    selectOnGit: [65, 66],
    selectOnVehicle: [73, 74, 75],
  },
  {
    id: "grapping_gun",
    name: "Grappling Gun",
    cost: 1,
    hands: 2,
    range: 9,
    dice: { type: "D8", count: 1 },
    damage: { weak: 0, big: 0 },
    rules: [{ id: "grappling_hook" }],
    selectOnGit: [67, 68],
    selectOnVehicle: [76, 77, 78],
  },
  {
    id: "hand_cannon",
    name: "Hand Cannon",
    cost: 2,
    hands: 1,
    range: 9,
    dice: { type: "D10", count: 1 },
    damage: { weak: 3, big: 5 },
    rules: [],
    selectOnGit: [69, 70],
    selectOnVehicle: [79],
  },
  {
    id: "grenades",
    name: "Grenades",
    cost: 2,
    hands: 0,
    range: 6,
    dice: { type: "D8", count: 1 },
    damage: { special: "EXPLODE" },
    rules: [{ id: "lob" }],
    selectOnGit: [71, 72],
    selectOnVehicle: [80, 81, 82],
  },
  {
    id: "molotovs",
    name: "Molotovs",
    cost: 2,
    hands: 0,
    range: 6,
    dice: { type: "D6", count: 1 },
    damage: { special: "EXPLODE" },
    rules: [{ id: "lob" }, { id: "fire" }],
    selectOnGit: [73, 74],
    selectOnVehicle: [83, 84],
  },
  {
    id: "flamethrower",
    name: "Flamethrower",
    cost: 2,
    hands: 2,
    range: 6,
    dice: { type: "D8", count: 3 },
    damage: { weak: 2, big: 3 },
    rules: [{ id: "gout" }, { id: "fire" }],
    selectOnGit: [75, 76],
    selectOnVehicle: [85, 86],
  },
  {
    id: "riot_cannon",
    name: "Riot Cannon",
    cost: 2,
    hands: 2,
    range: 9,
    dice: { type: "D6", count: 3 },
    damage: { weak: 1, big: 2 },
    rules: [{ id: "gout" }, { id: "jolt" }],
    selectOnGit: [77, 78],
    selectOnVehicle: [87, 88],
  },
  {
    id: "machine_gun",
    name: "Machine Gun",
    cost: 3,
    hands: 2,
    range: 12,
    dice: { type: "D6", count: 6 },
    damage: { weak: 1, big: 2 },
    rules: [],
    selectOnGit: [79, 80],
    selectOnVehicle: [89, 90],
  },
  {
    id: "mega_blaster",
    name: "Mega Blaster",
    cost: 3,
    hands: 2,
    range: 18,
    dice: { type: "D12", count: 1 },
    damage: { weak: 5, big: 10 },
    rules: [{ id: "cooldown" }],
    selectOnGit: [81],
    selectOnVehicle: [91, 92, 93, 94],
  },
  {
    id: "portal_gun",
    name: "Portal Gun",
    cost: 3,
    hands: 2,
    range: 18,
    dice: { type: "D6", count: 2 },
    damage: { weak: 0, big: 0 },
    rules: [{ id: "cooldown" }, { id: "portal" }],
    selectOnGit: [82],
    selectOnVehicle: [95, 96],
  },
  {
    id: "minigun",
    name: "Minigun",
    cost: 4,
    hands: 2,
    range: 12,
    dice: { type: "D6", count: 10 },
    damage: { weak: 1, big: 2 },
    rules: [{ id: "cooldown" }],
    selectOnGit: [83],
    selectOnVehicle: [97],
  },
  {
    id: "rocket_launcher",
    name: "Rocket Launcher",
    cost: 5,
    hands: 2,
    range: 18,
    dice: { type: "D10", count: 1 },
    damage: { special: "EXPLODE" },
    rules: [{ id: "cooldown" }, { id: "lob" }],
    selectOnGit: [84],
    selectOnVehicle: [98, 99],
  },

  // Melee

  {
    id: "crude_hand_weapon",
    name: "Crude Hand Weapon",
    cost: 0,
    hands: 1,
    range: 0,
    dice: { count: 2, type: "D6" },
    damage: { weak: 1, big: 2 },
    rules: [],
    selectOnGit: [],
    selectOnVehicle: [],
  },
  {
    id: "massive_hand_weapon",
    name: "Massive Hand Weapon",
    cost: 1,
    hands: 1,
    range: 0,
    dice: { count: 2, type: "D8" },
    damage: { weak: 2, big: 3 },
    rules: [],
    selectOnGit: [85, 86],
    selectOnVehicle: [],
  },
  {
    id: "flaming_torch",
    name: "Flaming Torch",
    cost: 1,
    hands: 1,
    range: 0,
    dice: { count: 3, type: "D6" },
    damage: { weak: 1, big: 2 },
    rules: [{ id: "fire" }],
    selectOnGit: [87, 88],
    selectOnVehicle: [],
  },
  {
    id: "ridiculously_long_broom",
    name: "Ridiculously Long Broom",
    cost: 1,
    hands: 2,
    range: 0,
    dice: { count: 3, type: "D6" },
    damage: { weak: 1, big: 2 },
    rules: [{ id: "poke" }],
    selectOnGit: [89, 90],
    selectOnVehicle: [],
  },
  {
    id: "electric_gitstick",
    name: "Electric Gitstick",
    cost: 2,
    hands: 1,
    range: 0,
    dice: { count: 2, type: "D8" },
    damage: { weak: 2, big: 3 },
    rules: [{ id: "jolt" }],
    selectOnGit: [91, 92],
    selectOnVehicle: [],
  },
  {
    id: "street_sign",
    name: "Street Sign",
    cost: 2,
    hands: 2,
    range: 0,
    dice: { count: 3, type: "D8" },
    damage: { weak: 2, big: 3 },
    rules: [],
    selectOnGit: [93, 94],
    selectOnVehicle: [],
  },
  {
    id: "buzzsaw",
    name: "Buzzsaw",
    cost: 3,
    hands: 1,
    range: 0,
    dice: { count: 1, type: "D10" },
    damage: { weak: 3, big: 5 },
    rules: [{ id: "buzzcut" }],
    selectOnGit: [95, 96],
    selectOnVehicle: [],
  },
  {
    id: "massive_buzzsaw",
    name: "Massive Buzzsaw",
    cost: 4,
    hands: 2,
    range: 0,
    dice: { count: 2, type: "D10" },
    damage: { weak: 3, big: 5 },
    rules: [{ id: "buzzcut" }],
    selectOnGit: [97, 98],
    selectOnVehicle: [],
  },
  {
    id: "mechanical_fist",
    name: "Mechanical Fist",
    cost: 4,
    hands: 1,
    range: 0,
    dice: { count: 1, type: "D12" },
    damage: { weak: 5, big: 10 },
    rules: [],
    selectOnGit: [99],
    selectOnVehicle: [],
  },
];

export const mods: ModProfile[] = [
  {
    id: "buzzblades",
    name: "Buzzblades",
    cost: 1,
    description:
      "If this vehicle is modelled with some vicious-looking sawblades, when you roll your smash dice, you count the target’s toughness as 1 lower.",
    selectOn: [1, 2],
  },
  {
    id: "electroshock_collision_warning_system",
    name: "Electroshock Collision Warning System",
    cost: 1,
    description:
      "If you smash into terrain, you cannot receive more than 3 damage from that smash.",
    selectOn: [3, 4],
  },
  {
    id: "experimental_teleporter",
    name: "Experimental Teleporter",
    cost: 3,
    description:
      "Instead of scooting, you can teleport. Flip a coin (or token or whatever) into the play area. It must travel up first, then down to the board. Place this model in contact with the coin, remove the coin, and rebuild its tower (the tower can’t fall down as a result of this teleport). If the coin is touching terrain or another model, or lands off the table, or your vehicle can’t fit in the new location: you don’t teleport and wipe out instead.",
    selectOn: [],
  },
  {
    id: "explosive_ram",
    name: "Explosive Ram",
    cost: 1,
    description:
      "If this vehicle is modelled with a dangerously unstable-looking ram, the first time in a game you smash, your smash dice do +2/+3 damage, but rolls of a 1 do 3 damage to you.",
    selectOn: [],
  },
  {
    id: "extra_seats",
    name: "Extra Seats",
    cost: 2,
    description: "The vehicle gains +2 seats.",
    selectOn: [],
    effects: [{ type: "INCREMENT_STAT", stat: "seats", value: 2 }],
  },
  {
    id: "flame_gouts",
    name: "Flame Gouts",
    cost: 2,
    description:
      "If you cause big damage to a model with your smash dice, it is set on fire and stays on fire until it falls over or wipes out. A model that is on fire suffers D3 damage when selected to activate.",
    selectOn: [],
  },
  {
    id: "force_field",
    name: "Force Field",
    cost: 1,
    description:
      "When this vehicle receives a damage die, roll it. If it rolls a 1, you can discard it, as it is reflected by the force field.",
    selectOn: [],
  },
  {
    id: "full_stocked_minibar",
    name: "Full-stocked Minibar",
    cost: 1,
    description:
      "After getting out of this vehicle, a git’s thumps deal +1/+1 damage until they fall over.",
    selectOn: [],
  },
  {
    id: "fuzzy_dice",
    name: "Fuzzy Dice",
    cost: 1,
    description:
      "Once per activation, may take a swig to re-roll any one scoot die.",
    selectOn: [],
  },
  {
    id: "git_launcher",
    name: "Git Launcher",
    cost: 1,
    description:
      "Also known as a “Git-a-pult”. Once per activation, when the vehicle shoots, one passenger may leave the vehicle (instead of shooting), moving as if they had a jetpack.",
    selectOn: [],
  },
  {
    id: "honking_great_shaker_scoop",
    name: "Honking Great Shaker Scoop",
    cost: 1,
    description:
      "If this vehicle has an engine scoop modelled on, one of the scoot dice you roll each activation can be one dice size higher than your engine stat, up to a maximum of D12.",
    selectOn: [],
  },
  {
    id: "hover_jets",
    name: "Hover-jets",
    cost: 2,
    description:
      "If this vehicle is modelled with some anti-grav boosters or fans or something, it rolls 4 scoot dice when it scoots.",
    selectOn: [],
  },
  {
    id: "huge_spoiler",
    name: "Huge Spoiler",
    cost: 1,
    description:
      "If this vehicle has a spoiler modelled on, when it scoots, it may discard a die from its tower for each of its scoot dice that roll 8+.",
    selectOn: [],
  },
  {
    id: "huge_wheels",
    name: "Huge Wheels",
    cost: 2,
    description:
      "If this vehicle has appropriate chonky tyres, while it has 4 or fewer dice in its tower, it cannot wipe out: if its tower falls, just restack it.",
    selectOn: [],
  },
  {
    id: "insanely_loud_sound_system",
    name: "Insanely Loud Sound System",
    cost: 2,
    description:
      "As an action, this vehicle can drop the bass: push every git within D6” up to 3” (you choose the order). If a pushed git bumps into another git, that second git is pushed directly away 3”. (If a pushed git ends up stuck in contact with an enemy end the action and a fight breaks out).",
    selectOn: [],
  },
  {
    id: "jet_engine",
    name: "Jet Engine",
    cost: 2,
    description:
      "If this vehicle has a ludicrous engine modelled on, any of your scoot dice that roll 8+ have their value doubled.",
    selectOn: [],
  },
  {
    id: "loads_of_spikes",
    name: "Loads of Spikes",
    cost: 1,
    description:
      "If this vehicle is all spikey-looking, you roll +2 smash dice.",
    selectOn: [],
  },
  {
    id: "massive_exhausts",
    name: "Massive Exhausts",
    cost: 2,
    description:
      "If this vehicle is modelled with extravagant exhaust pipes, all its scoot dice count as a minimum of 4, even if they roll lower.",
    selectOn: [],
  },
  {
    id: "massive_ram",
    name: "Massive Ram",
    cost: 1,
    description:
      "If this vehicle is modelled with a dirty big ram, its smash dice do +1/+1 damage.",
    selectOn: [],
  },
  {
    id: "mega_magnet",
    name: "Mega-magnet",
    cost: 2,
    description:
      "This mod counts as a pintle-mounted grappling gun, but rolls 1D12 as its bullet dice.",
    selectOn: [],
  },
  {
    id: "megaphone",
    name: "Megaphone",
    cost: 2,
    description:
      "If this vehicle is modelled with a megaphone or loudhailer, its driver can bark orders down it, as an action: Select a friendly git within 3D6”, they immediately take one action.",
    selectOn: [],
  },
  {
    id: "mystical_bumper_stickers",
    name: "Mystical Bumper Stickers",
    cost: 2,
    description:
      "If this vehicle has visible bumper stickers, once per activation it may take a swig to re-roll any of its smash dice.",
    selectOn: [],
  },
  {
    id: "mine_dropper",
    name: "Mine Dropper",
    cost: 1,
    description:
      "You start the game with 3 mines. Make little tokens or miniatures on 25mm bases. Once per scoot action, before rolling any scoot dice, you can place one of these tokens in contact with the back of your vehicle. If another model moves within 2” of this mine, pause their movement and the mine explodes (8). Then remove the mine from play and continue to their movement.",
    selectOn: [],
  },
  {
    id: "mountains_of_ammo",
    name: "Mountains of Ammo",
    cost: 1,
    description:
      "This vehicle may re-roll each of their bullet dice once per activation.",
    selectOn: [],
  },
  {
    id: "nitro_booster",
    name: "Nitro Booster",
    cost: 1,
    description:
      "When you scoot, you can boost instead: you only roll one scoot die, but you add +10” to it.",
    selectOn: [],
  },
  {
    id: "oil_slick_dropper",
    name: "Oil Slick Dropper",
    cost: 2,
    description:
      "You start the game with 3 oil slicks. Make little tokens or use 25mm bases. Once per scoot action, before rolling any scoot dice, you can place one of these tokens in contact with the back of your vehicle. Starting at the end of your activation, the area 3” around this token counts as Oil Slick terrain.",
    selectOn: [],
  },
  {
    id: "racing_stripes",
    name: "Racing Stripes",
    cost: 1,
    description:
      "If this model has an appropriately snazzy paint job, its scoot dice count their rolled values as 1 higher.",
    selectOn: [],
  },
  {
    id: "ramshackle_armour",
    name: "Ramshackle Armour",
    cost: 2,
    description:
      "When you take damage, roll a dice. Even: the damage is negated. Odd: the damage is negated, but the armour falls off and cannot be used again in this game.",
    selectOn: [],
  },
  {
    id: "sidesaws",
    name: "Sidesaws",
    cost: 1,
    description:
      "If this vehicle is modelled with suitable unpleasant looking lateral-mounted close-combat weapons, it gets a free smash before the first throw in each fight initiated against it by an enemy git.",
    selectOn: [],
  },
  {
    id: "smoke_belcher",
    name: "Smoke Belcher",
    cost: 1,
    description:
      "When hit by a bullet die, treat this vehicle’s toughness as 1 higher.",
    selectOn: [],
  },
  {
    id: "spiked_roller",
    name: "Spiked Roller",
    cost: 2,
    description:
      "If this vehicle is modelled with a massive death roller up front, when you cause big damage to your opponent with a smash, you can make another smash, counting the target’s toughness as +1 (and this can repeat, with the target’s toughness increasing by +1 each time).",
    selectOn: [],
  },
  {
    id: "tank_tracks",
    name: "Tank Tracks",
    cost: 1,
    description:
      "If this vehicle is modelled with tank tracks, once per scoot action it may discard one of its scoot dice to pivot to face any direction.",
    selectOn: [67, 68],
  },
  {
    id: "wrecking_ball",
    name: "Wrecking Ball",
    cost: 2,
    description:
      "Counts as a pintle-mounted weapon. When used, the vehicle explodes (6). (The vehicle itself isn’t damaged, this explosion represents the flailing steel ball).",
    selectOn: [],
  },
];

export const weaponSpecialRules: WeaponSpecialRule[] = [
  {
    id: "buzzcut",
    name: "Buzzcut",
    description:
      "If you cause big damage to your opponent in a fight, you can thump again with this weapon, counting the target’s toughness as +1. This can repeat, with the target’s toughness increasing by +1 each time).",
  },
  {
    id: "cooldown",
    name: "Cooldown",
    description:
      "If you shoot a weapon with the cooldown rule, this model cannot shoot it during its next activation. (You could mark the model with a token or something to help you remember this).",
  },
  {
    id: "fire",
    name: "Fire",
    description:
      "If you cause big damage to a model with this weapon, it is set on fire and stays on fire until it falls over or wipes out. A model that is on fire suffers an extra D3 damage when it takes a swig.",
  },
  {
    id: "gout",
    name: "Gout",
    description:
      "When resolving a bullet die from this weapon, pick one line of fire for it, this bullet hits everything in its path up to its range, not just the first thing.",
  },
  {
    id: "grappling_hook",
    name: "Grappling Hook",
    description:
      "If a bullet die from this weapon hits a cap, you loot it. If it hits a model: check the toughness of the two models: If your toughness is lower than theirs, you treat that bullet die as a scoot die and scoot. If your toughness is higher than theirs, push them towards you a number of inches equal to the value of the bullet die. If your toughnesses are the same, you choose which model moves (using the rules above). If it hits terrain, you scoot towards the bullet die.",
  },
  {
    id: "jolt",
    name: "Jolt",
    description:
      "If you cause big damage to a git with this weapon, they are pushed 3” directly away from the attacker by their own boss. If this push moves them into contact with another model or terrain, they fall down.",
  },
  {
    id: "poke",
    name: "Poke",
    description:
      "If you are armed with a weapon with poke, and end an activation within 2” of an enemy, you can push into contact with them to trigger a fight.",
  },
  {
    id: "lob",
    name: "Lob",
    description:
      "When resolving a bullet die from this weapon, ignore the line of fire. Instead, if the bullet die lies within the weapon’s range of the git that lobbed it, it explodes (x) (where x is equal to the bullet die roll). If it is out of range, discard it. If the bullet die is a dud (normally a “1”), the boss to your left places it exactly 1” away from the git that lobbed it. It explodes (x) there instead, where x is the highest possible face on the bullet die.",
  },
  {
    id: "portal",
    name: "Portal",
    description:
      "Roll this weapon’s two bullet dice separately. Before rolling, choose one to be the entrance die, and one to be the exit die (or, you know, use an orange die and a blue die). Draw a line of fire to the entrance die (extending up to the weapon’s range) The first git, barrel or cap to be hit is teleported. Place it in contact with the exit die (ignoring range and line of fire) and rebuild its tower next to it (the tower cannot fall down). Barrels immediately explode after being teleported.",
  },
];

export const treatments: TreatmentLocationProfile[] = [
  {
    id: "head",
    name: "Head",
    costPublic: 0,
    costPrivate: 3,
    selectOn: [1],
    treatments: [
      {
        id: "dead",
        name: "Dead",
        description:
          "This git is sadly departed, gone to join the choir invisible. Pour one out. They cannot be used again. You can sell their body to med-orc-al science for D3 caps.",
        selectOn: [1],
        effects: [{ type: "REMOVE_MODEL" }],
      },
      {
        id: "gnawbrain_xenotransplant",
        name: "Gnawbrain Xenotransplant",
        description:
          "This git cannot use weapons anymore. Any weapons they possessed when they showed up at A&E have been lost somewhere during the treatment.",
        selectOn: [2],
        effects: [],
      },
      {
        id: "steam_powered_calculating_engine",
        name: "Steam-powered Calculating Engine",
        description:
          "As a result of this brain “upgrade”, this git gains +2 Attitude.",
        selectOn: [3],
        effects: [{ type: "INCREMENT_STAT", stat: "attitude", value: 2 }],
      },
      {
        id: "rusty_tin_brain",
        name: "Rusty Tin Brain",
        description:
          "When you scoot, the boss to the left rolls one extra scoot die into the play area. You have to use the three highest dice.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "mechanical_respirator",
        name: "Mechanical Respirator",
        description:
          "This git makes sinister noises when it breathes. Gain 1 Attitude.",
        selectOn: [5],
        effects: [{ type: "INCREMENT_STAT", stat: "attitude", value: 1 }],
      },
      {
        id: "bionic_eye",
        name: "Bionic Eye",
        description:
          "If this git is modelled with a bionic eye then they may re-roll all their shooting dice once per activation.",
        selectOn: [6],
        effects: [],
      },
      {
        id: "horns",
        name: "Horns",
        description:
          "If this git is modelled with horns, or horned headgear, it gets a free headbutt before the first throw in each fight.",
        selectOn: [7],
        effects: [],
      },
      {
        id: "steel_jaw",
        name: "Steel Jaw",
        description:
          "If this git is modelled with a metal jaw or face, they get +1 Toughness when thumped in a fight.",
        selectOn: [8],
        effects: [],
      },
      {
        id: "skull_cap",
        fromGear: true,
        description:
          "If this git is modelled with a metal head of some sort, their headbutts roll 1D8 thump dice and do 2/3 damage.",
        selectOn: [9],
      },
      {
        id: "cybrain_2000",
        name: "Cybrain 2000",
        description:
          "Once per activation, rotate a dice you just rolled to a face of your choosing.",
        selectOn: [10],
        effects: [],
      },
    ],
  },
  {
    id: "arm",
    name: "Arm",
    isChiral: true,
    costPublic: 0,
    costPrivate: 2,
    selectOn: [2],
    treatments: [
      {
        id: "stump",
        name: "Stump",
        description: "This git now has one fewer hands.",
        selectOn: [1],
        effects: [],
      },
      {
        id: "wooden_hand",
        name: "Wooden Hand",
        description:
          "This arm still counts as having a hand (maybe they just gaffer tape a weapon to it), but they now steal 0 caps when they throw pinch in a fight.",
        selectOn: [2],
        effects: [],
      },
      {
        id: "rubbish_robotic_arm",
        name: "Rubbish Robotic Arm",
        description:
          "This hand can only be used to shoot guns, not use fighting weapons.",
        selectOn: [3],
        effects: [],
      },
      {
        id: "hook_hand",
        name: "Hook Hand",
        description:
          "If this git is modelled with a hook for a hand they have one fewer hand, but when they shove, they can move themselves (instead of your opponent) in any direction.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "chopper_arm",
        name: "Chopper Arm",
        description:
          "This git is now armed with a Massive Hand Weapon permanently held in this hand.",
        selectOn: [5],
        effects: [],
      },
      {
        id: "gun_arm",
        name: "Gun Arm",
        description:
          "Make a loot roll to find out what gun the doc has attached. This git is stuck with that weapon on that arm, and can’t hold anything else in it. (If the attached weapon needs 2 hands, he’ll still need to use his other arm to fire it).",
        selectOn: [6],
        effects: [],
      },
      {
        id: "extra_arm",
        name: "Extra Arm",
        description:
          "If this git is modelled with an extra arm of any sort, whether robotic or a mutation, they count as having an extra hand, and can attack with more weapons as a result.",
        selectOn: [7],
        effects: [],
      },
      {
        id: "spider_fingers",
        name: "Spider Fingers",
        description:
          "This git’s hand has been replaced with one with long spindly fingers. When they pinch in a fight, they pinch D3 caps.",
        selectOn: [8],
        effects: [],
      },
      {
        id: "telescopic_arm",
        name: "Telescopic Arm",
        description:
          "This git counts as being armed with a Grappling Gun that doesn’t require any hands.",
        selectOn: [9],
        effects: [],
      },
      {
        id: "beefy_robotic_fist",
        name: "Beefy Robotic Fist",
        description:
          "This git is now permanently armed with a Mechanical Fist in one hand.",
        selectOn: [10],
        effects: [],
      },
    ],
  },
  {
    id: "leg",
    name: "Leg",
    isChiral: true,
    costPublic: 0,
    costPrivate: 2,
    selectOn: [4],
    treatments: [
      {
        id: "wheeled_cart",
        name: "Wheeled Cart",
        description: "This git changes its scoot dice stat to 1D10.",
        selectOn: [1],
        effects: [
          { type: "SET_STAT", stat: "scoot", value: { count: 1, type: "D10" } },
        ],
      },
      {
        id: "peg_leg",
        name: "Peg Leg",
        description:
          "This git counts all its scoot dice values as one lower, to a minimum of 1”.",
        selectOn: [2],
        effects: [],
      },
      {
        id: "kneeless_servo_legs",
        name: "Kneeless Servo Legs",
        description: "This git changes its scoot dice stat to 2D6.",
        selectOn: [3],
        effects: [
          { type: "SET_STAT", stat: "scoot", value: { count: 2, type: "D6" } },
        ],
      },
      {
        id: "steel_arse",
        name: "Steel Arse",
        description:
          "It takes two actions (i.e. a whole activation) to boot this git up the arse.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "rusty_metal_leg",
        name: "Rusty Metal Leg",
        description:
          "This git may not scoot futher than 4” with a single scoot die, even if it has rolled higher.",
        selectOn: [5],
        effects: [],
      },
      {
        id: "power_knees",
        name: "Power-knees",
        description: "All this git’s scoot dice count as 1 higher.",
        selectOn: [6],
        effects: [],
      },
      {
        id: "bionic_legs",
        name: "Bionic Legs",
        description:
          "If this git is modelled with a robotic leg or legs of some sort, when you scoot, you may treat any scoot dice as a ‘3’.",
        selectOn: [7],
        effects: [],
      },
      {
        id: "tracks",
        name: "Tracks",
        description:
          "If this git is modelled with tracks instead of legs, when resolving a scoot dice, you may discard another of your scoot dice to add its value to the one being resolved.",
        selectOn: [8],
        effects: [],
      },
      {
        id: "mono_wheel",
        name: "Mono-wheel",
        description:
          "If this git is modelled with a wheel instead of legs, when you scoot, you can take a swig to discard one of your scoot dice and move its value in any direction.",
        selectOn: [9],
        effects: [],
      },
      {
        id: "rocket_legs",
        name: "Rocket Legs",
        description: "This git counts as carrying a Jetpack.",
        selectOn: [10],
        effects: [],
      },
    ],
  },
  {
    id: "body",
    name: "Body",
    costPublic: 0,
    costPrivate: 2,
    selectOn: [6, 7, 8],
    treatments: [
      {
        id: "dead",
        name: "Dead",
        description:
          "I’m terribly sorry. We did what we could, but I’m afraid his poor heart gave out on the operating table. They cannot be used again. You can sell their body to organ farmers for D3 caps.",
        selectOn: [1],
        effects: [{ type: "REMOVE_MODEL" }],
      },
      {
        id: "incredibly_unstable_mechanical_heart",
        name: "Incredibly Unstable Mechanical Heart",
        description:
          "If this model falls over it Explodes (X), where X is its toughness, and cannot stand up again this game.",
        selectOn: [2],
        effects: [],
      },
      {
        id: "glued_back_together",
        name: "Glued Back Together",
        description:
          "When you add a dice to this git’s tower, add an extra dice.",
        selectOn: [3],
        effects: [],
      },
      {
        id: "exposed_pipework",
        name: "Exposed Pipework",
        description: "This git suffers -1 toughness.",
        selectOn: [4],
        effects: [{ type: "INCREMENT_STAT", stat: "toughness", value: -1 }],
      },
      {
        id: "gnawball_liver_xenotransplant",
        name: "Gnawball Liver Xenotransplant",
        description: "When this git takes a swig, you add an extra dice.",
        selectOn: [5],
        effects: [],
      },
      {
        id: "steelgut_lockbox",
        name: "Steelgut Lockbox",
        description: "Enemy models cannot put the boot into this model.",
        selectOn: [6],
        effects: [],
      },
      {
        id: "spikey_bits",
        fromGear: true,
        description:
          "If this git is modelled with you-guessed-it, when fighting, if your thump is cancelled out by a shove, you still get to thump, but only using your headbutt thump die.",
        selectOn: [7],
      },
      {
        id: "metal_chest",
        name: "Metal Chest",
        description: "This git gains +1 toughness.",
        selectOn: [8],
        effects: [{ type: "INCREMENT_STAT", stat: "toughness", value: 1 }],
      },
      {
        id: "reboot_button",
        name: "Reboot Button",
        description:
          "When this git is down, it may still be selected to activate, but can only take a reboot action. Roll a D6. If you roll equal to or under their toughness, this it stands up. Regardless, its activation ends after the reboot action.",
        selectOn: [9],
        effects: [],
      },
      {
        id: "upgraded_to_a_klankbot!",
        name: "Upgraded to a Klankbot!",
        description:
          "This git is replaced with a Klankbot. They can keep all their stuff, it gets bolted on to their new metal body. If you roll this result for a Klankbot, they are upgraded to a Klankbot 2000™ and gain +2 Attitude instead.",
        selectOn: [10],
        effects: [
          {
            type: "SET_STAT",
            stat: "type",
            value: "klankbot",
          },
          {
            type: "INCREMENT_STAT",
            stat: "attitude",
            value: 2,
            when: { type: "klankbot" },
          },
        ],
      },
    ],
  },
];

export const repairs: RepairLocationProfile[] = [
  {
    id: "handling",
    name: "Handling",
    costBodgeJob: 0,
    costProperJob: 3,
    selectOn: [1],
    repairs: [
      {
        id: "write_off",
        name: "Write-off",
        description:
          "This vehicle is a write-off. Nothing to do but sell it for scrap. You lose the vehicle, but gain D3 caps.",
        selectOn: [1],
        effects: [{ type: "REMOVE_MODEL" }],
      },
      {
        id: "dodgy_brakes",
        name: "Dodgy Brakes",
        description:
          "Have to roll at least one of your highest possible dice type when moving",
        selectOn: [2],
        effects: [],
      },
      {
        id: "misaligned_wheels",
        name: "Misaligned Wheels",
        description:
          "Once per scoot action, your opponent can pick one of your scoot dice and force you to re-roll it.",
        selectOn: [3],
        effects: [],
      },
      {
        id: "cant_turn_left",
        name: "Can’t Turn Left",
        description:
          "Scoot dice on the left side of the vehicle can only be used to move forward, you can’t pivot with them.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "bent_axle",
        name: "Bent Axle",
        description:
          "When this vehicle wipes out, it automatically gets wrecked in the crunch step of the wipe out.",
        selectOn: [5],
        effects: [],
      },
      {
        id: "grinding_noise",
        name: "Grinding Noise",
        description:
          "Your scoot dice now all do 1/0 damage to you (e.g. 1 if you roll under your toughness).",
        selectOn: [6],
        effects: [],
      },
      {
        id: "twitchy_accelerator_pedal",
        name: "Twitchy Accelerator Pedal",
        description:
          "Once per scoot dice, if you bump into one of your scoot dice, roll it again as a new scoot dice.",
        selectOn: [7],
        effects: [],
      },
      {
        id: "huge_wheels",
        fromMod: true,
        selectOn: [8],
      },
      {
        id: "tank_tracks",
        fromMod: true,
        selectOn: [9],
      },
      {
        id: "hover_jets",
        fromMod: true,
        selectOn: [10],
      },
    ],
  },
  {
    id: "guts",
    name: "Guts",
    costBodgeJob: 0,
    costProperJob: 2,
    selectOn: [2],
    repairs: [
      {
        id: "write_off",
        name: "Write-off",
        description:
          "This vehicle is a write-off. Nothing to do but sell it for scrap. You lose the vehicle, but gain D3 caps.",
        selectOn: [1],
        effects: [{ type: "REMOVE_MODEL" }],
      },
      {
        id: "worn_gasket",
        name: "Worn Gasket",
        description:
          "Worsen engine stat by one dice rank (e.g. D8 becomes D6).",
        selectOn: [2],
        effects: [{ type: "INCREMENT_STAT", stat: "engine", value: -1 }],
      },
      {
        id: "rusty_gearbox",
        name: "Rusty Gearbox",
        description:
          "When you roll your scoot dice, they have to all be the same rank of dice.",
        selectOn: [3],
        effects: [],
      },
      {
        id: "leaking_fuel_line",
        name: "Leaking Fuel Line",
        description:
          "Everyone on board is constantly breathing fumes. When the driver takes a swig, they take D3 dice.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "sluggish_accelerator",
        name: "Sluggish Accelerator",
        description: "Roll one fewer scoot dice.",
        selectOn: [5],
        effects: [],
      },
      {
        id: "high_pressure_fuel_lines",
        name: "High Pressure Fuel Lines",
        description:
          "In order to fix a partially blocked fuel line, Krankshaft cranks up the pressure to dangerous levels. If this vehicle gets wrecked it explodes (10).",
        selectOn: [6],
        effects: [],
      },
      {
        id: "honking_great_shaker_scoop",
        fromMod: true,
        selectOn: [7],
      },
      {
        id: "nitro_booster",
        fromMod: true,
        selectOn: [8],
      },
      {
        id: "jet_engine",
        fromMod: true,
        selectOn: [9],
      },
      {
        id: "experimental_teleporter",
        fromMod: true,
        selectOn: [10],
      },
    ],
  },
  {
    id: "snazz",
    name: "Snazz",
    costBodgeJob: 0,
    costProperJob: 2,
    selectOn: [3],
    repairs: [
      {
        id: "offensive_repaint",
        name: "Offensive Repaint",
        description: "You have to repaint your model in a different colour.",
        selectOn: [1],
        effects: [],
      },
      {
        id: "really_bright_headlamps",
        name: "Really Bright Headlamps",
        description:
          "When you bump into a git, they can roll 1D6 scoot before you smash them, and if they clear your path, they aren’t smashed.",
        selectOn: [2],
        effects: [],
      },
      {
        id: "snazzy_underlighting",
        name: "Snazzy Underlighting",
        description:
          "Enemy bullet dice that land within 6” of this vehicle can be re-rolled by their shooter.",
        selectOn: [3],
        effects: [],
      },
      {
        id: "booming_exhausts",
        name: "Booming Exhausts",
        description:
          "At the end of this vehicle’s activation, every git (friend or foe) within 3” of it takes a swig.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "huge_spoiler",
        fromMod: true,
        selectOn: [5],
      },
      {
        id: "mysterious_red_button",
        name: "Mysterious Red Button",
        description:
          "Once per game, at the start of your activation, you can press the button: roll a D6. On a 1, you wipeout. On a 2-5, you count as equipped with a Jet Engine, mod for the rest of the activation. On a 6, you count as equipped with an Experimental Teleporter mod for the rest of the activation.",
        selectOn: [6],
        effects: [],
      },
      {
        id: "sidesaws",
        fromMod: true,
        selectOn: [7],
      },
      {
        id: "massive_exhausts",
        fromMod: true,
        selectOn: [8],
      },
      {
        id: "racing_stripes",
        fromMod: true,
        selectOn: [9],
      },
      {
        id: "insanely_loud_sound_system",
        fromMod: true,
        selectOn: [10],
      },
    ],
  },
  {
    id: "hull",
    name: "Hull",
    costBodgeJob: 0,
    costProperJob: 3,
    selectOn: [4, 5],
    repairs: [
      {
        id: "write_off",
        name: "Write-off",
        description:
          "This vehicle is a write-off. Nothing to do but sell it for scrap. You lose the vehicle, but gain D3 caps.",
        selectOn: [1],
        effects: [{ type: "REMOVE_MODEL" }],
      },
      {
        id: "buckled_pillars",
        name: "Buckled Pillars",
        description:
          "This vehicle suffers -1 Toughness permanently (to a minimum of 1).",
        selectOn: [2],
        effects: [{ type: "INCREMENT_STAT", stat: "toughness", value: -1 }],
      },
      {
        id: "crushed_roof",
        name: "Crushed Roof",
        description:
          "This vehicle suffers -1 Toughness permanently (to a minimum of 1).",
        selectOn: [3],
        effects: [{ type: "INCREMENT_STAT", stat: "toughness", value: -1 }],
      },
      {
        id: "mangled_chassis",
        name: "Mangled Chassis",
        description: "This vehicle suffers -1 Seat permanently.",
        selectOn: [4],
        effects: [{ type: "INCREMENT_STAT", stat: "seats", value: -1 }],
      },
      {
        id: "heavy_ass_armour",
        name: "Heavy-ass Armour",
        description:
          "This vehicle gains +1 Toughness, but reduces its Engine stat by one dice rank.",
        selectOn: [5],
        effects: [
          { type: "INCREMENT_STAT", stat: "toughness", value: 1 },
          { type: "INCREMENT_STAT", stat: "engine", value: -1 },
        ],
      },
      {
        id: "armour_made_of_old_doors",
        name: "Armour Made of Old Doors",
        description: "They have a load of handles. +2 Seats",
        selectOn: [6],
        effects: [{ type: "INCREMENT_STAT", stat: "seats", value: 2 }],
      },
      {
        id: "sidesaws",
        fromMod: true,
        selectOn: [7],
      },
      {
        id: "loads_of_spikes",
        fromMod: true,
        selectOn: [8],
      },
      {
        id: "chopper_blades_refit",
        name: "Chopper Blades Refit",
        description:
          "This vehicle can fly, using the same rules as the Buzzcopter.",
        selectOn: [9],
        effects: [],
      },
      {
        id: "scrap_tank_refit",
        name: "Scrap Tank Refit",
        description:
          "Upgraded to a Scrap Tank! This vehicle is replaced with a Scrap Tank. It can keep all its stuff.",
        selectOn: [10],
        effects: [{ type: "SET_STAT", stat: "type", value: "scrap_tank" }],
      },
    ],
  },
  {
    id: "weapons",
    name: "Weapons",
    costBodgeJob: 0,
    costProperJob: 2,
    selectOn: [6],
    repairs: [
      {
        id: "wrecked_weapon",
        name: "Wrecked Weapon",
        description:
          "This vehicle loses a randomly selected weapon permanently.",
        selectOn: [1],
        effects: [],
      },
      {
        id: "ill_considered_weapon_venting",
        name: "Ill-considered Weapon Venting",
        description:
          "When you shoot, you take one damage for each 6+ you roll.",
        selectOn: [2],
        effects: [],
      },
      {
        id: "damaged_weapon_mounts",
        name: "Damaged Weapon Mounts",
        description: "This vehicle’s bullet dice suffer -1 to their rolls.",
        selectOn: [3],
        effects: [],
      },
      {
        id: "weak_frame",
        name: "Weak Frame",
        description: "You roll 2 fewer smash dice when you smash.",
        selectOn: [4],
        effects: [],
      },
      {
        id: "overheating_ammo_belts",
        name: "Overheating Ammo Belts",
        description:
          "When firing this vehicle’s weapons, any bullet dice that rolls a 2 or under is a dud.",
        selectOn: [5],
        effects: [],
      },
      {
        id: "smoke_belcher",
        fromMod: true,
        selectOn: [6],
      },
      {
        id: "auto_gunner",
        name: "Auto-gunner",
        description:
          "Once per activation, when the driver fires a fixed weapon, they can re-roll their bullet dice.",
        selectOn: [7],
        effects: [],
      },
      {
        id: "wrecking_ball",
        fromMod: true,
        selectOn: [8],
      },
      {
        id: "targeting_array",
        name: "Targeting Array",
        description: "This vehicle’s bullet dice rolls count as 1 higher.",
        selectOn: [9],
        effects: [],
      },
      {
        id: "pleasant_surprise",
        name: "Pleasant Surprise",
        description:
          "Make a vehicle loot roll. That’s what Krankshaft has added to your vehicle.",
        selectOn: [10],
        effects: [],
      },
    ],
  },
];

export const vehicleSpecialRules: VehicleSpecialRule[] = [
  {
    id: "buzzcopter",
    name: "Buzzcopter",
    description:
      "The Buzzcopter flies above the ground. When a Buzzcopter moves it ignores intervening terrain or models: just pick it up and zoom it over the top, leaving its tower behind. Roll a D6 for each bottle cap you pass over: you loot it on a 4+. Finally, push the tower back into contact with the model (along whatever route is needed), making sure not to knock it over while you do.",
  },
];

export const factions: FactionProfile[] = [
  {
    id: "the_fuzz",
    name: "The Fuzz",
    fluff: "",
    rules: [
      {
        id: "whats_going_on_ere_then",
        name: "What’s Going On ‘Ere Then?",
        description:
          "When this git activates, you may push it up to 3” directly towards the last enemy model to activate, then take your two actions.",
      },
    ],
  },
  {
    id: "grill_cooks",
    name: "Grill Cooks",
    fluff: "",
    rules: [
      {
        id: "cook_out",
        name: "Cook Out",
        description:
          "This git may take an action to bbq: Place a cap from your stash within 2”, not touching another model. Friendly fallen gits within 3” of the cap stand up. Then leave it in play as a normal cap and end this model’s activation.",
      },
    ],
  },
  {
    id: "riot_mimes",
    name: "Riot Mimes",
    fluff: "",
    rules: [
      {
        id: "invisible_walls",
        name: "Invisible Walls",
        description:
          "This git may imagine a wall as an action: take up to 5 tower dice from enemy gits within 6”. Line those dice up to form a straight wall, at least partially within 3” of this git. This wall stops models’ movement, but doesn’t stop bullets. Discard the wall from play if the model that created it is ever more than 6” from it, or they make another.",
      },
    ],
  },
  {
    id: "city_boys",
    name: "City Boys",
    fluff: "",
    rules: [
      {
        id: "look_after_the_pennies",
        name: "Look After The Pennies",
        description:
          "Once per activation, this git may discard a scoot die to push 3” towards any in-play cap.",
      },
    ],
  },
  {
    id: "old_boys",
    name: "Old Boys",
    fluff: "",
    rules: [
      {
        id: "ptsd",
        name: "PTSD",
        description:
          "This git may be selected to activate, even when fallen: their first action is to stand up and gain 2D6 damage. If they roll a double, they are removed from play, and count as ending the game fallen.",
      },
    ],
  },
  {
    id: "techno_vikings",
    name: "Techno Vikings",
    fluff: "",
    rules: [
      {
        id: "berserk",
        name: "Berserk",
        description:
          "If this git ends a scoot dice move within 3” of an enemy model, it may push into contact with that model (if it can do so in a straight line).",
      },
    ],
  },
  {
    id: "git_rock",
    name: "Git Rock",
    fluff: "",
    rules: [
      {
        id: "mosh_pit",
        name: "Mosh Pit",
        description:
          "When this model is pushed by any game effect, you may increase the distance of the push by 1”.",
      },
    ],
  },
  {
    id: "bad_docs",
    name: "Bad Docs",
    fluff: "",
    rules: [
      {
        id: "pulling_teeth",
        name: "Pulling Teeth",
        description:
          "This git may take a swig to loot D3 caps (instead of 1) when it puts the boot in.",
      },
    ],
  },
  {
    id: "agitators",
    name: "Agitators",
    fluff: "",
    rules: [
      {
        id: "unity_my_comrades",
        name: "Unity, My Comrades!",
        description:
          "When this git rolls a natural 6 on a scoot dice, any other friendly standing gits may take a swig to push up to 6” toward that die.",
      },
    ],
  },
  {
    id: "the_green_wing",
    name: "The Green Wing",
    fluff: "",
    rules: [
      {
        id: "go_for_the_weak",
        name: "Go For The Weak",
        description:
          "When an enemy git within 6” falls over, this git may take a swig to push 3” toward the fallen git.",
      },
    ],
  },
  {
    id: "swabbies",
    name: "Swabbies",
    fluff: "",
    rules: [
      {
        id: "street_sweeper",
        name: "Street Sweeper",
        description:
          "May take the sweep action: select an in-play cap within 6” and scoot it, rolling 3D6 scoot dice for it.",
      },
    ],
  },
];

export const orcNames = rawOrcNames
  .split("\n")
  .map((l) => l.split(" ").map((n) => n.trim()) as NameTriple);

export const gnawballNames = rawGnawballNames
  .split("\n")
  .map((l) => l.split(" ").map((n) => n.trim()) as NameTriple);

export const generators: NameGeneratorData[] = [
  {
    id: "git",
    name: "Git",
    names: orcNames,
    joinPattern: "{start} {middle}{end}",
  },

  {
    id: "gnawball",
    name: "Gnawball",
    names: gnawballNames,
    joinPattern: "{start}{middle}{end}",
  },

  {
    id: "gang",
    name: "Gang",
    joinPattern: "{start} {middle} {end}",
    names: [
      ["Power", "Street", "Runners"],
      ["Danger", "City", "Breakers"],
      ["Shred", "Road", "Burglers"],
      ["Buckle", "Tower", "Gang"],
      ["Brown", "Block", "Punks"],
      ["Green", "Falls", "Crumpets"],
      ["Busted", "Mutton", "Mob"],
      ["Nut", "Mold", "Dummies"],
      ["Hard", "land", "Band"],
      ["Breaker", "Shaft", "Crims"],
      ["Ball", "Stand", "Busters"],
      ["East", "Deck", "Unit"],
      ["West", "Dock", "Clanks"],
      ["North", "Trap", "Cranks"],
      ["South", "Pile", "Festival"],
      ["Low", "Pit", "Fortunes"],
      ["Urk", "Way", "Pickers"],
      ["Gnork", "Boiled", "Blunts"],
      ["Narg", "Bag", "Boys"],
      ["Grundy", "Street", "Chumps"],
      ["Grunk", "Bite", "Bankers"],
      ["Slart", "Barf", "Crusties"],
      ["Ponge", "Blow", "Teamsters"],
      ["Ferp", "Town", "Family"],
      ["Azorka", "Tent", "Mess"],
      ["Shittikka", "Blessed", "Muscle"],
      ["Kroff", "Booze", "Nutters"],
      ["Pliss", "Brand", "Office"],
      ["Randy", "Munch", "Incorporate"],
      ["Narflun", "Pike", "Raiders"],
      ["Rotten", "Crotch", "Rentiers"],
      ["Trash", "Pants", "Wallflowers"],
      ["Toxic", "Boot", "Wailers"],
      ["Slink", "Bank", "Workers"],
      ["Prangk", "Pipe", "Wankers"],
      ["Gas", "Cross", "Washups"],
      ["Oil", "Catch", "Scrots"],
      ["Grunge", "Rise", "Scroats"],
      ["Slop", "Truck", "Skimmers"],
      ["Sludge", "Train", "Crunchers"],
      ["Scum", "Track", "Felons"],
      ["Sewer", "Trail", "Groupies"],
      ["Dust", "Tour", "Gaggle"],
      ["Dirt", "Berg", "Muddle"],
      ["Blunt", "County", "Crew"],
      ["Smoke", "Country", "Wreckers"],
      ["Yellow", "Bridge", "Rollers"],
      ["Rough", "Bound", "Pirates"],
      ["Slime", "Bush", "& Co."],
      ["Rust", "Mountain", "& Associates"],
      ["Grunt", "Island", "& Sons"],
      ["Grab", "River", "Shifters"],
      ["Swanky", "Bath", "Gits"],
      ["Sour", "Bowl", "Avengers"],
      ["Stink", "House", "Wanderers"],
      ["Cracked", "Court", "Gigglers"],
      ["Crooked", "Lane", "Garglers"],
      ["Bluster", "Alley", "Boozers"],
      ["Blank", "Crescent", "Reserves"],
      ["Ol'", "End", "Oiks"],
      ["Death", "Fork", "Freaks"],
      ["Shite", "City", "Misfits"],
      ["Knob", "City", "Sniffers"],
      ["Broken", "City", "Mission"],
      ["Rat", "City", "Wipers"],
      ["Red", "Street", "Scum"],
      ["Bone", "Bucket", "Union"],
      ["Blue", "Brew", "Slickers"],
      ["Groat", "Town", "Traders"],
      ["Can", "Note", "Auxiliary"],
      ["Tin", "Back", "Grifters"],
    ],
  },
];
